import { type MouseEvent as ReactMouseEvent, type RefObject, useCallback, useEffect, useRef } from 'react';

import { type ZoomToPointParams } from '~/components/AssetModal/Visualizer/ImageVisualizer/shared/types';

import { MAX_ZOOM, MIN_ZOOM } from '../shared/constants';

const SCROLL_VELOCITY = 0.2;
const TOUCH_VELOCITY = 0.9;
const DEFAULT_TRANSITION = 0.25;
const DECELERATION_DURATION = 750;

// Determines how many pixels the image should be moved.
const getLimitedShift = ({
  shift,
  minLimit,
  maxLimit,
  minElement,
  maxElement,
}: {
  shift: number;
  minLimit: number;
  maxLimit: number;
  minElement: number;
  maxElement: number;
}) => {
  if (shift > 0) {
    if (minElement > minLimit) {
      // Forbid move if we are moving to left or top while we are already out minimum boudaries
      return 0;
    } else if (minElement + shift > minLimit) {
      // Lower the shift if we are going out boundaries
      return minLimit - minElement;
    }
  } else if (shift < 0) {
    if (maxElement < maxLimit) {
      // Forbid move if we are moving to right or bottom while we are already out maximum boudaries
      return 0;
    } else if (maxElement + shift < maxLimit) {
      // Lower the shift if we are going out boundaries
      return maxLimit - maxElement;
    }
  }

  return shift;
};

export type UseImageZoomProps = {
  zoom: number;
  onZoomIn: (x: number, y: number) => void;
  onZoomOut: (x: number, y: number) => void;
  imageRef: RefObject<HTMLImageElement>;
  imgContainerRef: RefObject<HTMLDivElement>;
};

export const useImageZoom = ({ zoom, onZoomIn, onZoomOut, imageRef, imgContainerRef }: UseImageZoomProps) => {
  const lastRequestAnimationId = useRef<number | null>(null);
  const lastCursor = useRef<{ posX: number; posY: number } | null>(null);
  const lastShift = useRef<{ x: number; y: number } | null>(null);
  const posXRef = useRef<number>(0);
  const posYRef = useRef<number>(0);
  const zoomRef = useRef<number>(MIN_ZOOM);
  const transitionDuration = useRef<number>(DEFAULT_TRANSITION);
  const isMoving = useRef<boolean>(false);

  // Repositions whatever imgRef is attached to.
  const repositionElement = useCallback(
    ({
      newPosY,
      newPosX,
      newTransitionDuration,
      newZoom,
    }: {
      newPosY: number;
      newPosX: number;
      newTransitionDuration?: number;
      newZoom?: number;
    }) => {
      if (newZoom && newZoom !== zoomRef.current) {
        zoomRef.current = newZoom;
      }

      if (newTransitionDuration !== undefined && newTransitionDuration !== transitionDuration.current) {
        transitionDuration.current = newTransitionDuration;
        if (imageRef.current && imgContainerRef.current) {
          imageRef.current.style.transition = `transform ease-out ${newTransitionDuration}s`;
          imgContainerRef.current.style.transition = `transform ease-out ${newTransitionDuration}s`;
        }
      }

      posXRef.current = newPosX;
      posYRef.current = newPosY;
      if (imgContainerRef.current) {
        imgContainerRef.current.style.transform = `translate3d(${newPosX}px, ${newPosY}px, 0)`;
      }
      if (imageRef.current) {
        imageRef.current.style.transform = `scale(${newZoom || zoomRef.current})`;
      }
    },
    [imageRef, imgContainerRef],
  );

  // Function that returns position coordinates if zooming in / out of a specific mouse point.
  const getNewPosition = useCallback(
    (x: number, y: number, newZoom: number) => {
      const prevPosY = posYRef.current;
      const prevPosX = posXRef.current;

      if (newZoom === 1) {
        return [0, 0];
      }

      if (newZoom > zoomRef.current && imgContainerRef.current) {
        const rect = imgContainerRef.current.getBoundingClientRect();

        // Get the center of the image container.
        const [centerX, centerY] = [rect.width / 2, rect.height / 2];
        // Get the current mouse position.
        const [relativeX, relativeY] = [x - rect.left - window.pageXOffset, y - rect.top - window.pageYOffset];

        // If zooming in, center the mouse position
        const [absX, absY] = [(centerX - relativeX) / zoomRef.current, (centerY - relativeY) / zoomRef.current];
        const ratio = newZoom - zoomRef.current;
        return [prevPosX + absX * ratio, prevPosY + absY * ratio];
      } else {
        // If zooming out, return to center
        return [(prevPosX * (newZoom - 1)) / (zoomRef.current - 1), (prevPosY * (newZoom - 1)) / (zoomRef.current - 1)];
      }
    },
    [imgContainerRef],
  );

  // Max zooms into a specific mouse position
  const zoomInOnPosition = useCallback(
    (x: number, y: number, zoom: number) => {
      const sanitizedZoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
      const [posX, posY] = getNewPosition(x, y, sanitizedZoom);

      repositionElement({
        newZoom: sanitizedZoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: DEFAULT_TRANSITION,
      });
    },
    [getNewPosition, repositionElement],
  );

  // Moves the zoomed in image within its boundaries.
  const move = useCallback(
    (shiftX: number, shiftY: number) => {
      if (!imageRef.current || !imgContainerRef.current?.parentNode) return;
      let posX = posXRef.current;
      let posY = posYRef.current;

      // Get container and container's parent coordinates
      const rect = imageRef.current.getBoundingClientRect();
      const parentRect = (imgContainerRef.current.parentNode as HTMLDivElement).getBoundingClientRect();

      const [isLarger, isOutLeftBoundary, isOutRightBoundary] = [
        // Check if the element is larger than its container
        rect.width > parentRect.right - parentRect.left,
        // Check if the element is out its container left boundary
        shiftX > 0 && rect.left - parentRect.left < 0,
        // Check if the element is out its container right boundary
        shiftX < 0 && rect.right - parentRect.right > 0,
      ];

      const canMoveOnX = isLarger || isOutLeftBoundary || isOutRightBoundary;
      if (canMoveOnX) {
        posX += getLimitedShift({
          shift: shiftX,
          minLimit: parentRect.left,
          maxLimit: parentRect.right,
          minElement: rect.left,
          maxElement: rect.right,
        });
      }

      const [isHigher, isOutTopBoundary, isOutBottomBoundary] = [
        // Check if the element is higher than its container
        rect.height > parentRect.bottom - parentRect.top,
        // Check if the element is out its container top boundary
        shiftY > 0 && rect.top - parentRect.top < 0,
        // Check if the element is out its container bottom boundary
        shiftY < 0 && rect.bottom - parentRect.bottom > 0,
      ];

      const canMoveOnY = isHigher || isOutTopBoundary || isOutBottomBoundary;

      if (canMoveOnY) {
        posY += getLimitedShift({
          shift: shiftY,
          minLimit: parentRect.top,
          maxLimit: parentRect.bottom,
          minElement: rect.top,
          maxElement: rect.bottom,
        });
      }

      repositionElement({
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: 0,
      });
    },
    [imageRef, imgContainerRef, repositionElement],
  );

  //Slows down the move, giving it a feeling of momentum.
  const startDeceleration = useCallback(
    (lastShiftOnX: number, lastShiftOnY: number) => {
      let startTimestamp: number | null = null;

      const moveFrame = (timestamp: number) => {
        if (startTimestamp === null) {
          startTimestamp = timestamp;
        }
        const progress = timestamp - startTimestamp;

        // Calculates the ratio to apply on the move (used to create a non-linear deceleration)
        const ratio = (DECELERATION_DURATION - progress) / DECELERATION_DURATION;

        const [shiftX, shiftY] = [lastShiftOnX * ratio, lastShiftOnY * ratio];

        // Continue animation only if time has not expired and if there is still some movement (more than 1 pixel on one axis)
        if (progress < DECELERATION_DURATION && Math.max(Math.abs(shiftX), Math.abs(shiftY)) > 1) {
          move(shiftX, shiftY);
          lastRequestAnimationId.current = requestAnimationFrame(moveFrame);
        } else {
          lastRequestAnimationId.current = null;
        }
      };

      lastRequestAnimationId.current = requestAnimationFrame(moveFrame);
    },
    [move],
  );

  // Zooms in / out of the current mouse position.
  const handleMouseWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();

      // @ts-ignore
      const useTouchVelocity = event.ctrlKey || Math.abs(event.wheelDeltaY) % 120 !== 0;

      // Use the scroll event delta to determine the zoom velocity
      const velocity = (-event.deltaY * (useTouchVelocity ? TOUCH_VELOCITY : SCROLL_VELOCITY)) / 100;

      // Call the appropriate zoom function based on velocity
      if (velocity > 0) {
        onZoomIn(event.pageX, event.pageY);
      } else if (velocity < 0) {
        onZoomOut(event.pageX, event.pageY);
      }
    },
    [onZoomIn, onZoomOut],
  );

  const zoomToPoint = useCallback(
    ({ x, y, zoom, transitionDuration = 0.05 }: ZoomToPointParams) => {
      const [posX, posY] = getNewPosition(x, y, zoom);
      repositionElement({
        newZoom: zoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: transitionDuration,
      });
    },
    [getNewPosition, repositionElement],
  );

  // Resets to default zoom.
  const reset = useCallback(() => {
    repositionElement({
      newZoom: MIN_ZOOM,
      newPosX: 0,
      newPosY: 0,
      newTransitionDuration: DEFAULT_TRANSITION,
    });
  }, [repositionElement]);

  const handleDoubleClick = useCallback(
    (event: MouseEvent) => {
      // Amount zoomed by each double click
      const zoomFactor = 5;

      event.preventDefault();
      if (zoomRef.current < MAX_ZOOM) {
        zoomInOnPosition(event.pageX, event.pageY, zoomRef.current * zoomFactor);
      } else {
        reset();
      }
    },
    [zoomInOnPosition, reset],
  );

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (zoomRef.current === MIN_ZOOM || !isMoving.current) return;
      event.preventDefault();

      if (!lastCursor.current) {
        return;
      }

      const [posX, posY] = [event.pageX, event.pageY];
      const shiftX = posX - lastCursor.current.posX;
      const shiftY = posY - lastCursor.current.posY;

      move(shiftX, shiftY);
      lastCursor.current = { posX, posY };
      lastShift.current = { x: shiftX, y: shiftY };
    },
    [move],
  );

  const handleMouseStart = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (zoomRef.current !== MIN_ZOOM && !isMoving.current) isMoving.current = true;

    if (lastRequestAnimationId.current) {
      cancelAnimationFrame(lastRequestAnimationId.current);
    }

    lastCursor.current = { posX: event.pageX, posY: event.pageY };
  }, []);

  const handleMouseStop = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (isMoving.current) isMoving.current = false;

      if (lastShift.current) {
        // Use the last shift to make a decelerating movement effect
        startDeceleration(lastShift.current.x, lastShift.current.y);
        lastShift.current = null;
      }

      lastCursor.current = null;
    },
    [startDeceleration],
  );

  const stopMoving = useCallback(() => {
    isMoving.current = false;
    lastCursor.current = null;
    lastShift.current = null;
  }, []);

  // Update zoomRef and reposition when zoom changes
  useEffect(() => {
    if (zoom !== zoomRef.current) {
      const prevZoom = zoomRef.current;
      zoomRef.current = zoom;

      // Calculate new positions based on zoom change
      let posX = posXRef.current;
      let posY = posYRef.current;

      if (zoom !== MIN_ZOOM) {
        posX = (posX * (zoom - 1)) / (prevZoom > 1 ? prevZoom - 1 : prevZoom);
        posY = (posY * (zoom - 1)) / (prevZoom > 1 ? prevZoom - 1 : prevZoom);
      } else {
        // Reset to center when zooming out to minimum
        posX = 0;
        posY = 0;
      }

      repositionElement({
        newZoom: zoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: DEFAULT_TRANSITION,
      });
    }
  }, [zoom, repositionElement]);

  // Initialize event handlers
  useEffect(() => {
    if (imgContainerRef.current && imageRef.current) {
      imageRef.current.style.transition = `transform ease-out ${DEFAULT_TRANSITION}s`;
      imgContainerRef.current.style.transition = `transform ease-out ${DEFAULT_TRANSITION}s`;
      imgContainerRef.current.ondblclick = handleDoubleClick;
      imgContainerRef.current.onwheel = handleMouseWheel;
    }
  }, [handleDoubleClick, handleMouseWheel, imageRef, imgContainerRef]);

  return {
    zoomToPoint,
    reset,
    handleMouseMove,
    handleMouseStart,
    handleMouseStop,
    stopMoving,
    isMoving: isMoving.current,
  };
};
